import { motion } from "framer-motion";
import {
  Users,
  UserCheck,
  Brain,
  Workflow,
  Wallet,
  Boxes,
  BarChart3,
  ListChecks,
  Layers,
} from "lucide-react";

const features = [
  {
    icon: Users,
    title: "Lead & Sales Management",
    desc: "Capture, qualify, and convert. Pipelines configured exactly to how your team sells — not a generic template.",
    span: "md:col-span-5",
    height: "min-h-[280px]",
  },
  {
    icon: UserCheck,
    title: "Client Tracking",
    desc: "360° client profiles, history, communications, and contracts in one place.",
    span: "md:col-span-4",
    height: "min-h-[280px]",
  },
  {
    icon: Brain,
    title: "AI Summaries & Insights",
    desc: "Daily standups, deal risks, and account briefs generated automatically.",
    span: "md:col-span-3",
    height: "min-h-[280px]",
  },
  {
    icon: Workflow,
    title: "Workflow Automation",
    desc: "Custom triggers, approvals, and routing. Your processes — codified, never compromised.",
    span: "md:col-span-4",
    height: "min-h-[260px]",
  },
  {
    icon: Wallet,
    title: "Expense & Employee Management",
    desc: "Payroll inputs, reimbursements, attendance, performance — unified.",
    span: "md:col-span-4",
    height: "min-h-[260px]",
  },
  {
    icon: Boxes,
    title: "Inventory / SKU Management",
    desc: "Multi-warehouse, batch, expiry — adapted to your stock model.",
    span: "md:col-span-4",
    height: "min-h-[260px]",
  },
  {
    icon: BarChart3,
    title: "Analytics & Reporting",
    desc: "Custom dashboards. The KPIs that actually run your business — visualized for every role.",
    span: "md:col-span-6",
    height: "min-h-[300px]",
    featured: true,
  },
  {
    icon: ListChecks,
    title: "Task & Operations",
    desc: "Project boards, ops checklists, SLA tracking — built around your team's rhythm.",
    span: "md:col-span-3",
    height: "min-h-[300px]",
  },
  {
    icon: Layers,
    title: "Custom Modules",
    desc: "Need something nobody else has? We build it. Specifically for your industry.",
    span: "md:col-span-3",
    height: "min-h-[300px]",
  },
];

export default function Features() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="bg-white text-neutral-950 py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-neutral-500">
              01 — Platform
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter mt-6 leading-[1.05]">
              One platform.
              <br />
              <span className="text-neutral-400">Infinitely yours.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-lg text-neutral-600 font-light leading-relaxed">
              Every dashboard, workflow, automation, report, module — tailored to
              your exact operational reality. Software should adapt to your business,
              not the other way around.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08, ease: "easeOut" }}
                className={`${f.span} ${f.height} relative group rounded-2xl border border-neutral-200 bg-neutral-50 hover:bg-white hover:border-neutral-900 transition-all duration-500 p-7 md:p-8 flex flex-col justify-between overflow-hidden hover:-translate-y-1`}
                data-testid={`feature-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-neutral-900 text-white flex items-center justify-center group-hover:rotate-3 transition-transform duration-500">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                <div>
                  <h3 className="font-display text-2xl md:text-[26px] font-normal tracking-tight leading-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="text-sm md:text-base text-neutral-600 font-light leading-relaxed max-w-md">
                    {f.desc}
                  </p>
                </div>

                {f.featured && (
                  <div className="absolute -right-12 -bottom-12 w-48 h-48 rounded-full border border-neutral-300 opacity-50 group-hover:scale-110 transition-transform duration-700" />
                )}
              </motion.div>
            );
          })}
        </div>

        <p className="mt-12 text-center text-sm tracking-[0.2em] uppercase text-neutral-500">
          Everything customizable — to your business, your way.
        </p>
      </div>
    </section>
  );
}
